import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import URI from 'urijs';
import {
  getFromSessionStorage,
  setInSessionStorage,
} from 'Client/session-storage-api/sessionStorageApi';
import config from 'Config';
import { useAppContext } from 'Context/ReactAppContext';
import { useScriptLoader } from 'Src/hooks/use-script-loader/useScriptLoader';
import { t } from 'Src/i18n';
import { userInfoSelector } from 'Store/user-info/userInfo.selectors';
import { formatApiDate } from 'Utils/date/apiDate';
import { asSessionStorageRecord } from 'Src/types/view-helpers';
const esfScriptUrl = config.get('esf.scriptUrl') as string;
const GO_BACK_HASH = '#goBack';
const CUSTOMER = 'Customer';
export const ESF_WIDGET_ID = 'esfWidget';

type MseMWidget = {
  esf: (options: unknown, presets: unknown) => void;
  onLoad: (callback: () => void) => void;
  tunnel: (options: unknown, presets: unknown) => void;
};

const getMseM = (): MseMWidget => window.MseM as MseMWidget;

const formatApiDateforEsf = (date: APIDate) => formatApiDate(date, 'YYYY-MM-DD');

type Props = {
  showEsfTunnel: boolean;
  stay: StayEntity;
};
export const EsfWidget = ({ showEsfTunnel, stay }: Props) => {
  const { locale } = useAppContext();
  const { scriptLoaded, scriptLoadingFailed } = useScriptLoader(esfScriptUrl);
  const router = useRouter();
  const profile: UserInfo = useSelector(userInfoSelector);
  const currentUri = new URI(router.asPath);
  const options = {
    channel: config.get('esf.channel'),
    fullWidth: true,
    groundedTo: `#${ESF_WIDGET_ID}`,
    lang: locale.includes('fr') ? undefined : 'en',
    noMarketing: true,
    resort: stay.product.esfResortId,
    verticalMargin: 100,
  };
  const presets = {
    lodging: {
      name: stay.product.name,
      position: [stay.product.coordinates?.latitude, stay.product.coordinates?.longitude],
    },
    stay: {
      from: formatApiDateforEsf(stay.dates.startDate),
      to: formatApiDateforEsf(stay.dates.endDate),
    },
  };

  const cartChanged = () => {
    getMseM().tunnel({ ...options, esfUrl: GO_BACK_HASH }, presets);
  };

  const esfOptions = { ...options, cartChanged: cartChanged, intermediary: 'SITE_CM' };

  const hashChangeHandler = (e) => {
    if (e.newURL.includes(GO_BACK_HASH)) {
      router.replace(currentUri.clone().hash('').resource());
      getMseM().esf(esfOptions, presets);
    }
  };

  useEffect(() => {
    window.addEventListener('hashchange', hashChangeHandler, false);
    setInSessionStorage(CUSTOMER, {
      address1: profile.address
        ? `${profile.address.streetNumber} ${profile.address.streetName}`
        : '',
      city: profile.address?.city || '',
      country: profile.address?.countryCode || '',
      email: profile.email || '',
      firstname: profile.firstName || '',
      lastname: profile.lastName || '',
      mobile: profile.mobilePhoneNumber || '',
      optinMail: !!profile.optinEmail,
      postalcode: profile.address?.zipCode || '',
      title: '',
      ...asSessionStorageRecord(getFromSessionStorage(CUSTOMER)),
    });
    setInSessionStorage('lodging', {
      name: stay.product.name,
      position: [stay.product.coordinates?.latitude, stay.product.coordinates?.longitude],
    });
    return () => {
      window.removeEventListener('hashchange', hashChangeHandler, false);
    };
  }, []);
  useEffect(() => {
    if (!scriptLoaded) {
      return;
    }

    getMseM().onLoad(() => {
      if (showEsfTunnel) {
        getMseM().tunnel({ ...options, esfUrl: GO_BACK_HASH }, presets);
      } else {
        getMseM().esf(esfOptions, presets);
      }
    });
  }, [scriptLoaded]);

  if (scriptLoadingFailed) {
    return (
      <div className="mt-40 text-center font-bold text-red">
        {t('stays.upsells.esf.errors.genericErrorLabel')}
      </div>
    );
  }

  return <div id={ESF_WIDGET_ID} />;
};
